import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useGetCharactersQuery, type CharacterArgs } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useSelectedCharacterId } from './use-selected-character-id';

export interface CurrentCharacterId {
    /** The character the page shows: this tab's selection if it is one of the account's characters, else the first; null before they load, or with none. */
    characterId: string | null;
    /** The account's characters are still loading. */
    isLoading: boolean;
}

/**
 * The character a page is about: the selected one if it is among
 * `characters`, else the first. `undefined` before they load, or with none.
 */
export function findCurrentCharacter<T extends { id: string }>(characters: readonly T[] | undefined, selectedId: string | null): T | undefined {
    return characters?.find((character) => character.id === selectedId) ?? characters?.[0];
}

/**
 * Which character the page is about, the way Character Home picks it. Reads
 * only the cached character list, so hooks that need just the id (a
 * journal, a cutscene) don't pull in the character's full state.
 */
export function useCurrentCharacterId(): CurrentCharacterId {
    const accountId = getStoredCanonicalAccountId();
    const { selectedCharacterId } = useSelectedCharacterId();
    const { data: characters, isLoading } = useGetCharactersQuery(accountId ?? skipToken);

    return { characterId: findCurrentCharacter(characters, selectedCharacterId)?.id ?? null, isLoading };
}

/**
 * The signed-in account and current character as the store's character-scoped
 * query args, or `skipToken` while either is unknown (signed out, characters
 * still loading, an account with none). Pass it straight to a query hook.
 */
export function useCurrentCharacterArgs(): CharacterArgs | typeof skipToken {
    const accountId = getStoredCanonicalAccountId();
    const { characterId } = useCurrentCharacterId();
    return useMemo(
        () => (accountId === null || characterId === null ? skipToken : { accountId, characterId }),
        [accountId, characterId],
    );
}
